import { notificationApi } from '../api/notification.api';
import { deviceInfo } from './device';

/**
 * Web push, end to end:
 *
 *   1. read the Firebase web config + VAPID key from VITE_ env vars
 *   2. register firebase-messaging-sw.js (config passed via query string)
 *   3. ask Firebase for THIS browser's push token (permission must be granted)
 *   4. hand the token to the API -> user_devices.push_token / push_provider
 *
 * Everything is best-effort: missing env vars, denied permission, an
 * unsupported browser - each simply means "no web push", never a broken app.
 * The in-app bell keeps working regardless.
 */

function firebaseWebConfig() {
  const env = import.meta.env;
  const config = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };
  const vapidKey = env.VITE_FIREBASE_VAPID_KEY;
  if (!config.apiKey || !config.projectId || !config.messagingSenderId || !config.appId || !vapidKey) {
    return null; // not configured - push stays off, silently
  }
  return { config, vapidKey };
}

/** Logout resets the once-per-session guard - the next login (possibly a
 *  DIFFERENT user on this browser) must claim the token afresh. */
export function resetWebPushRegistration(): void {
  registeredThisSession = false;
}

/** Remember per-browser that we already registered, to skip repeat work. */
let registeredThisSession = false;

/**
 * True when the ONLY thing between this browser and push is the person's
 * one-time Allow: config present, browser capable, permission still
 * "default" (never asked, or the browser prompt was dismissed). The app
 * then shows its own visible prompt whose CLICK carries the user gesture
 * browsers require before they show the real permission dialog (Safari:
 * always requires it; Chrome: shows the full prompt instead of the quiet
 * bell icon).
 */
export function canOfferWebPush(): boolean {
  if (registeredThisSession) return false;
  if (!firebaseWebConfig()) return false;
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  return Notification.permission === 'default';
}

/** Why push is or is not active for this browser, for the Settings UI. */
export type WebPushStatus =
  | 'registered'    // token saved - pushes reach this browser
  | 'unconfigured'  // VITE_FIREBASE_* env vars missing
  | 'unsupported'   // browser has no service worker / Push API support
  | 'denied'        // user blocked notifications for this site
  | 'failed';       // Firebase or API error (already logged)

/**
 * Call after login (and on app start for an existing session). Registers the
 * push token with the API when everything lines up; quietly does nothing
 * otherwise. Returns WHY it stopped so Settings can show a real status.
 *
 * NOTE for Safari: the permission prompt only ever appears when this runs
 * from a user gesture (a click). The automatic call after login therefore
 * silently stops at 'denied'/'default' there - the Settings button is the
 * path Safari users must take once.
 */
export async function enableWebPush(): Promise<WebPushStatus> {
  if (registeredThisSession) return 'registered';
  // Single-flight: the once-only guard above only latches when a run
  // FINISHES, so two overlapping calls (StrictMode's dev double-mount, or
  // the prompt card clicked while the login call is still running) would
  // both slip through and each rotate a token at FCM. Concurrent callers
  // now share the one in-flight run instead.
  if (inFlightEnable) return inFlightEnable;
  inFlightEnable = doEnableWebPush().finally(() => {
    inFlightEnable = null;
  });
  return inFlightEnable;
}

let inFlightEnable: Promise<WebPushStatus> | null = null;

async function doEnableWebPush(): Promise<WebPushStatus> {
  const setup = firebaseWebConfig();
  if (!setup) return 'unconfigured';
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return 'unsupported';

  // Ask only when undecided; respect an explicit Block forever.
  if (Notification.permission === 'default') {
    const answer = await Notification.requestPermission();
    if (answer !== 'granted') return 'denied';
  }
  if (Notification.permission !== 'granted') return 'denied';

  try {
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
    if (!(await isSupported())) return 'unsupported';

    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?config=${encodeURIComponent(JSON.stringify(setup.config))}`,
    );

    const app = initializeApp(setup.config);
    const token = await getToken(getMessaging(app), {
      vapidKey: setup.vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) return 'failed';

    await notificationApi.registerDevice({
      ...deviceInfo(),
      pushToken: token,
      pushProvider: 'FCM',
    });
    registeredThisSession = true;
    console.log('[push] web push token registered');
    return 'registered';
  } catch (error) {
    console.warn('[push] could not enable web push:', error);
    return 'failed';
  }
}
